
import React from 'react';

// Mapping of indicator names to backend implementation status
const indicatorStatus = {
  // Moving Averages
  'SMA': true,
  'EMA': true,
  'TEMA': true,
  'DEMA': true,
  'KAMA': false,
  // Oscillators
  'RSI': true,
  'WILLIAMS_R': true,
  'CCI': true,
  'STOCH_K': false,
  'STOCH_D': false,
  'UO': false,
  'TSI': false,
  'FISHER': false,
  // Trend
  'SUPERTREND': true,
  'PSAR': true,
  'ADX': true,
  'AROON_UP': false,
  'AROON_DOWN': false,
  'AROON_OSC': false,
  'TRIX': false,
  // Volatility
  'ATR': true,
  'BB_UPPER': true,
  'BB_MIDDLE': true,
  'BB_LOWER': true,
  'KC_UPPER': false,
  'KC_LOWER': false,
  // Volume
  'MFI': false,
  'CMF': false,
  'VWAP': false,
  'OBV': true,
  // Other
  'ROC': true,
  'MOMENTUM': true,
  'PPO': false,
  'MASS_INDEX': false,
  'BULL_POWER': false,
  'BEAR_POWER': false,
};

function IndicatorTag({ name }) {
  const implemented = indicatorStatus[name];
  return (
    <span style={{
      marginLeft: 8,
      padding: '2px 8px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      background: implemented ? '#e8f5e9' : '#ffebee',
      color: implemented ? '#388e3c' : '#b71c1c',
      border: `1px solid ${implemented ? '#388e3c' : '#b71c1c'}`,
      verticalAlign: 'middle',
      display: 'inline-block',
    }}>
      {implemented ? 'Implemented' : 'Not Implemented'}
    </span>
  );
}

export default function UserGuidePage({ currentTheme, isDarkTheme }) {
  return (
    <div style={{
      backgroundColor: currentTheme.cardBg,
      border: `1px solid ${currentTheme.border}`,
      borderRadius: '8px',
      padding: '24px',
      color: currentTheme.textPrimary,
      fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
      boxShadow: isDarkTheme ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.07)'
    }}>
      <div style={{ 
        fontSize: '22px', 
        fontWeight: 700, 
        marginBottom: '20px', 
        color: currentTheme.textPrimary,
        borderBottom: `2px solid ${currentTheme.border}`,
        paddingBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontFamily: 'inherit'
      }}>
        📋 Complete User Guide - Indicators & Mathematical Expressions
      </div>
      <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
        <p style={{ marginBottom: '20px', color: currentTheme.textSecondary }}>
          Learn how to create powerful trading conditions using mathematical expressions! 
          You can now use parentheses, math operations, and combine multiple indicators.
        </p>

        {/* Basic Price Data */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ color: '#4285f4', fontSize: '18px', marginBottom: '12px' }}>📊 Price Data You Can Use</h3>
          <div style={{ 
            backgroundColor: isDarkTheme ? '#3a3a3a' : '#f8f9fa',
            padding: '15px',
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '14px',
            border: `1px solid ${currentTheme.border}`
          }}>
            <div style={{ marginBottom: '8px' }}><strong>CLOSE</strong> - Current closing price</div>
            <div style={{ marginBottom: '8px' }}><strong>HIGH</strong> - Highest price of the period</div>
            <div style={{ marginBottom: '8px' }}><strong>LOW</strong> - Lowest price of the period</div>
            <div style={{ marginBottom: '8px' }}><strong>OPEN</strong> - Opening price of the period</div>
            <div><strong>VOLUME</strong> - Trading volume</div>
          </div>
        </div>

        {/* Math Operations */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ color: '#4285f4', fontSize: '18px', marginBottom: '12px' }}>➕ Math Operations You Can Use</h3>
          <div style={{ 
            backgroundColor: isDarkTheme ? '#3a3a3a' : '#f8f9fa',
            padding: '15px',
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '14px',
            border: `1px solid ${currentTheme.border}`
          }}>
            <div style={{ marginBottom: '8px' }}><strong>+</strong> Addition (e.g., SMA(20) + 5)</div>
            <div style={{ marginBottom: '8px' }}><strong>-</strong> Subtraction (e.g., CLOSE - ATR(14))</div>
            <div style={{ marginBottom: '8px' }}><strong>*</strong> Multiplication (e.g., SMA(20) * 1.03)</div>
            <div style={{ marginBottom: '8px' }}><strong>/</strong> Division (e.g., CLOSE / SMA(200))</div>
            <div><strong>( )</strong> Parentheses for grouping operations</div>
          </div>
        </div>

        {/* Binary Indicators - Understanding 1 and 0 */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ color: '#9c27b0', fontSize: '18px', marginBottom: '12px' }}>🎯 Binary Indicators - Understanding 1 and 0</h3>
          <div style={{ 
            backgroundColor: isDarkTheme ? '#3a2a3a' : '#f8f0ff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #9c27b0',
            marginBottom: '15px'
          }}>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>🎓 What are Binary Indicators?</strong>
              <p style={{ marginTop: '8px', color: currentTheme.textSecondary, fontSize: '14px', lineHeight: '1.5' }}>
                Some indicators don't give you numbers like RSI (0-100) or prices. Instead, they give you simple <strong>1</strong> or <strong>0</strong> values:
              </p>
              <ul style={{ marginTop: '8px', color: currentTheme.textSecondary, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.6' }}>
                <li><strong style={{ color: '#34a853' }}>1 = TRUE/BULLISH/BUY SIGNAL</strong></li>
                <li><strong style={{ color: '#ea4335' }}>0 = FALSE/BEARISH/SELL SIGNAL</strong></li>
              </ul>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>📊 Supertrend Example:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '12px',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '13px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                <div style={{ marginBottom: '8px', color: '#34a853' }}>
                  <strong>SUPERTREND(10, 3.0)</strong> returns:<br/>
                  • <strong>1</strong> when trend is BULLISH (price above Supertrend line)<br/>
                  • <strong>0</strong> when trend is BEARISH (price below Supertrend line)
                </div>
              </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>💡 How to Use Binary Indicators:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '12px',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '13px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#34a853' }}>Entry:</span> SUPERTREND(10, 3.0)<br/>
                  <span style={{ fontSize: '11px', color: currentTheme.textSecondary }}>↳ Buy when it returns 1 (bullish)</span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#ea4335' }}>Exit:</span> SUPERTREND(10, 3.0) == 0<br/>
                  <span style={{ fontSize: '11px', color: currentTheme.textSecondary }}>↳ Sell when it returns 0 (bearish)</span>
                </div>
              </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>🔧 Other Binary Indicators:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '12px',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '12px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`,
                lineHeight: '1.6'
              }}>
                <div><strong>SUPERTREND(period, mult)</strong> - 1=bullish, 0=bearish</div>
                <div><strong>SUPERTREND_BULL(period, mult)</strong> - 1=bullish, 0=not bullish</div>
                <div><strong>SUPERTREND_BEAR(period, mult)</strong> - 1=bearish, 0=not bearish</div>
              </div>
            </div>
            <div>
              <strong style={{ color: currentTheme.textPrimary }}>⚠️ Common Student Mistakes:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#3f2a2a' : '#ffe8e8',
                padding: '12px',
                borderRadius: '6px',
                marginTop: '8px',
                border: '1px solid #ea4335'
              }}>
                <div style={{ fontSize: '13px', color: isDarkTheme ? '#ffaaaa' : '#cc0000', lineHeight: '1.5' }}>
                  <div style={{ marginBottom: '8px' }}>
                    ❌ <strong>Wrong:</strong> SUPERTREND(10, 3.0) &gt; 50<br/>
                    <span style={{ fontSize: '11px' }}>↳ Supertrend doesn't return numbers like 50!</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    ✅ <strong>Correct:</strong> SUPERTREND(10, 3.0) == 1<br/>
                    <span style={{ fontSize: '11px' }}>↳ Check if it's bullish (returns 1)</span>
                  </div>
                  <div>
                    ✅ <strong>Even Better:</strong> SUPERTREND(10, 3.0)<br/>
                    <span style={{ fontSize: '11px' }}>↳ Since 1=true, you don't need == 1</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ 
            backgroundColor: isDarkTheme ? '#2a3f2a' : '#e8f5e8',
            padding: '15px',
            borderRadius: '6px',
            border: '1px solid #34a853'
          }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#34a853', marginBottom: '8px' }}>
              🎯 Quick Reference for Students:
            </div>
            <div style={{ fontSize: '13px', color: currentTheme.textSecondary, lineHeight: '1.6' }}>
              • <strong>When you see == 1:</strong> "Is this indicator bullish?"<br/>
              • <strong>When you see == 0:</strong> "Is this indicator bearish?"<br/>
              • <strong>Just the indicator name:</strong> Same as == 1 (true when bullish)<br/>
              • <strong>Think of it like a switch:</strong> ON (1) = bullish, OFF (0) = bearish
            </div>
          </div>
        </div>

        {/* Examples Section */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ color: '#34a853', fontSize: '18px', marginBottom: '12px' }}>💡 Real Trading Examples</h3>
          {/* Percentage Examples */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ color: currentTheme.textPrimary, fontSize: '16px', marginBottom: '10px' }}>
              🎯 Percentage-Based Conditions
            </h4>
            <div style={{ 
              backgroundColor: isDarkTheme ? '#2a3f2a' : '#e8f5e8',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #34a853',
              marginBottom: '8px'
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: '14px', marginBottom: '4px' }}>
                CLOSE &gt; (SMA(20) * 1.03)
              </div>
              <div style={{ fontSize: '12px', color: '#2d5016' }}>
                ✅ Buy when price is 3% above the 20-day moving average
              </div>
            </div>
            <div style={{ 
              backgroundColor: isDarkTheme ? '#3f2a2a' : '#ffe8e8',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #ea4335',
              marginBottom: '8px'
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: '14px', marginBottom: '4px' }}>
                CLOSE &lt; (SMA(50) * 0.97)
              </div>
              <div style={{ fontSize: '12px', color: '#5f2120' }}>
                🛑 Sell when price drops 3% below the 50-day moving average
              </div>
            </div>
          </div>
          {/* Dynamic Stop Loss */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ color: currentTheme.textPrimary, fontSize: '16px', marginBottom: '10px' }}>
              🛡️ Dynamic Stop Losses
            </h4>
            <div style={{ 
              backgroundColor: isDarkTheme ? '#3f2a2a' : '#ffe8e8',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #ea4335'
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: '14px', marginBottom: '4px' }}>
                CLOSE &lt; (SMA(20) - ATR(14) * 2)
              </div>
              <div style={{ fontSize: '12px', color: '#5f2120' }}>
                🛑 Exit when price falls 2 ATR units below the moving average
              </div>
            </div>
          </div>
          {/* Relative Strength */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ color: currentTheme.textPrimary, fontSize: '16px', marginBottom: '10px' }}>
              💪 Relative Strength Indicators
            </h4>
            <div style={{ 
              backgroundColor: isDarkTheme ? '#2a3f2a' : '#e8f5e8',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #34a853'
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: '14px', marginBottom: '4px' }}>
                ((CLOSE - LOW) / (HIGH - LOW)) &gt; 0.8
              </div>
              <div style={{ fontSize: '12px', color: '#2d5016' }}>
                ✅ Buy when price is in the top 20% of the day's range
              </div>
            </div>
          </div>
          {/* Combined Indicators */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ color: currentTheme.textPrimary, fontSize: '16px', marginBottom: '10px' }}>
              🔄 Combined Indicators
            </h4>
            <div style={{ 
              backgroundColor: isDarkTheme ? '#2a3f2a' : '#e8f5e8',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #34a853'
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: '14px', marginBottom: '4px' }}>
                CLOSE &gt; (SMA(20) + ATR(14) * 1.5)
              </div>
              <div style={{ fontSize: '12px', color: '#2d5016' }}>
                ✅ Buy when price breaks above MA plus 1.5 times volatility
              </div>
            </div>
          </div>
          {/* Percentage Returns */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ color: currentTheme.textPrimary, fontSize: '16px', marginBottom: '10px' }}>
              📈 Percentage Return Calculations
            </h4>
            <div style={{ 
              backgroundColor: isDarkTheme ? '#2a3f2a' : '#e8f5e8',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #34a853'
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: '14px', marginBottom: '4px' }}>
                ((CLOSE / SMA(20)) - 1) * 100 &gt; 5
              </div>
              <div style={{ fontSize: '12px', color: '#2d5016' }}>
                ✅ Buy when current price is 5% or more above the 20-day average
              </div>
            </div>
          </div>
        </div>

        {/* Tips for Students */}
        {/* Complete Indicators Reference */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ color: '#9c27b0', fontSize: '20px', marginBottom: '20px' }}>📊 Complete Indicators Reference</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {/* Moving Averages */}
            <div style={{ 
              backgroundColor: isDarkTheme ? '#2a3a2a' : '#f0f8f0',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #34a853'
            }}>
              <h4 style={{ color: '#34a853', fontSize: '16px', marginBottom: '15px' }}>📈 Moving Averages</h4>
              <div style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.8' }}>
                <div><strong>SMA(period)</strong> <IndicatorTag name="SMA" /> - Simple Moving Average</div>
                <div><strong>EMA(period)</strong> <IndicatorTag name="EMA" /> - Exponential Moving Average</div>
                <div><strong>TEMA(period)</strong> <IndicatorTag name="TEMA" /> - Triple Exponential MA</div>
                <div><strong>DEMA(period)</strong> <IndicatorTag name="DEMA" /> - Double Exponential MA</div>
                <div><strong>KAMA(period, fast, slow)</strong> <IndicatorTag name="KAMA" /> - Adaptive MA</div>
              </div>
            </div>
            {/* Oscillators */}
            <div style={{ 
              backgroundColor: isDarkTheme ? '#3a2a2a' : '#fff0f0',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #ea4335'
            }}>
              <h4 style={{ color: '#ea4335', fontSize: '16px', marginBottom: '15px' }}>🌊 Oscillators</h4>
              <div style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.8' }}>
                <div><strong>RSI(period)</strong> <IndicatorTag name="RSI" /> - Relative Strength Index</div>
                <div><strong>WILLIAMS_R(period)</strong> <IndicatorTag name="WILLIAMS_R" /> - Williams %R</div>
                <div><strong>CCI(period)</strong> <IndicatorTag name="CCI" /> - Commodity Channel Index</div>
                <div><strong>STOCH_K(k, d)</strong> <IndicatorTag name="STOCH_K" /> - Stochastic %K</div>
                <div><strong>STOCH_D(k, d)</strong> <IndicatorTag name="STOCH_D" /> - Stochastic %D</div>
                <div><strong>UO(p1, p2, p3)</strong> <IndicatorTag name="UO" /> - Ultimate Oscillator</div>
                <div><strong>TSI(long, short)</strong> <IndicatorTag name="TSI" /> - True Strength Index</div>
                <div><strong>FISHER(period)</strong> <IndicatorTag name="FISHER" /> - Fisher Transform</div>
              </div>
            </div>
            {/* Trend Indicators */}
            <div style={{ 
              backgroundColor: isDarkTheme ? '#3a2a3a' : '#f8f0ff',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #9c27b0'
            }}>
              <h4 style={{ color: '#9c27b0', fontSize: '16px', marginBottom: '15px' }}>📊 Trend Indicators</h4>
              <div style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.8' }}>
                <div><strong>SUPERTREND(period, mult)</strong> <IndicatorTag name="SUPERTREND" /> - Supertrend</div>
                <div><strong>PSAR(af_start, af_inc, af_max)</strong> <IndicatorTag name="PSAR" /> - Parabolic SAR</div>
                <div><strong>ADX(period)</strong> <IndicatorTag name="ADX" /> - Average Directional Index</div>
                <div><strong>AROON_UP(period)</strong> <IndicatorTag name="AROON_UP" /> - Aroon Up</div>
                <div><strong>AROON_DOWN(period)</strong> <IndicatorTag name="AROON_DOWN" /> - Aroon Down</div>
                <div><strong>AROON_OSC(period)</strong> <IndicatorTag name="AROON_OSC" /> - Aroon Oscillator</div>
                <div><strong>TRIX(period)</strong> <IndicatorTag name="TRIX" /> - TRIX</div>
              </div>
            </div>
            {/* Volatility Indicators */}
            <div style={{ 
              backgroundColor: isDarkTheme ? '#3a3a2a' : '#fff8f0',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #ff9800'
            }}>
              <h4 style={{ color: '#ff9800', fontSize: '16px', marginBottom: '15px' }}>📊 Volatility</h4>
              <div style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.8' }}>
                <div><strong>ATR(period)</strong> <IndicatorTag name="ATR" /> - Average True Range</div>
                <div><strong>BB_UPPER(period, std)</strong> <IndicatorTag name="BB_UPPER" /> - Bollinger Upper</div>
                <div><strong>BB_MIDDLE(period, std)</strong> <IndicatorTag name="BB_MIDDLE" /> - Bollinger Middle</div>
                <div><strong>BB_LOWER(period, std)</strong> <IndicatorTag name="BB_LOWER" /> - Bollinger Lower</div>
                <div><strong>KC_UPPER(period, mult)</strong> <IndicatorTag name="KC_UPPER" /> - Keltner Upper</div>
                <div><strong>KC_LOWER(period, mult)</strong> <IndicatorTag name="KC_LOWER" /> - Keltner Lower</div>
              </div>
            </div>
            {/* Volume Indicators */}
            <div style={{ 
              backgroundColor: isDarkTheme ? '#2a3a3a' : '#f0f8ff',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #2196f3'
            }}>
              <h4 style={{ color: '#2196f3', fontSize: '16px', marginBottom: '15px' }}>📊 Volume</h4>
              <div style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.8' }}>
                <div><strong>MFI(period)</strong> <IndicatorTag name="MFI" /> - Money Flow Index</div>
                <div><strong>CMF(period)</strong> <IndicatorTag name="CMF" /> - Chaikin Money Flow</div>
                <div><strong>VWAP</strong> <IndicatorTag name="VWAP" /> - Volume Weighted Average Price</div>
                <div><strong>OBV</strong> <IndicatorTag name="OBV" /> - On Balance Volume</div>
              </div>
            </div>
            {/* Other Indicators */}
            <div style={{ 
              backgroundColor: isDarkTheme ? '#3a3a3a' : '#f8f8f8',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #607d8b'
            }}>
              <h4 style={{ color: '#607d8b', fontSize: '16px', marginBottom: '15px' }}>🔧 Other Indicators</h4>
              <div style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.8' }}>
                <div><strong>ROC(period)</strong> <IndicatorTag name="ROC" /> - Rate of Change</div>
                <div><strong>MOMENTUM(period)</strong> <IndicatorTag name="MOMENTUM" /> - Momentum</div>
                <div><strong>PPO(fast, slow)</strong> <IndicatorTag name="PPO" /> - Price Oscillator</div>
                <div><strong>MASS_INDEX(period, ema)</strong> <IndicatorTag name="MASS_INDEX" /> - Mass Index</div>
                <div><strong>BULL_POWER(period)</strong> <IndicatorTag name="BULL_POWER" /> - Elder Ray Bull</div>
                <div><strong>BEAR_POWER(period)</strong> <IndicatorTag name="BEAR_POWER" /> - Elder Ray Bear</div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ 
          backgroundColor: isDarkTheme ? '#2a2f3f' : '#e8f0fe',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #4285f4'
        }}>
          <h3 style={{ color: '#4285f4', fontSize: '18px', marginBottom: '15px' }}>🎓 Tips for Students</h3>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li style={{ marginBottom: '8px' }}>
              <strong>Start Simple:</strong> Begin with basic conditions like <code>CLOSE &gt; SMA(20)</code>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong>Use Parentheses:</strong> Always use parentheses to make your math clear: <code>(SMA(20) * 1.03)</code>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong>Percentage Moves:</strong> Multiply by 1.05 for 5% above, or 0.95 for 5% below
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong>Combine Ideas:</strong> Mix indicators like <code>SMA(20) + ATR(14)</code> for dynamic levels
            </li>
            <li>
              <strong>Test Everything:</strong> Always backtest your mathematical expressions to see how they work!
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
